import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"
import Dropzone from "dropzone"

// Disable Dropzone auto-discovery to prevent conflicts
Dropzone.autoDiscover = false

export default class extends Controller {
  static values = {
    url: String,
    uploadableId: String,
    uploadableType: String,
    canCreate: Boolean,
    baseUrl: String,
    maxFileSize: String
  }

  static targets = [
    "container", "dropzone", "dropzoneContainer", "filesList", "fileItem"
  ]

  connect() {
    this.uploads = []
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    
    // Create modal in body (like Vue Teleport)
    this.setupModal()
    
    // Load initial files
    this.loadFiles()
    
    // Setup escape key handler for modal
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
    
    // Force Dropzone auto-discovery to run again for this element
    this.ensureDropzoneInitialized()
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
    
    // Clean up modal from body
    if (this.bodyModalWrapper) {
      document.body.removeChild(this.bodyModalWrapper)
      this.bodyModalWrapper = null
      this.bodyModalTitle = null
      this.bodyModalDownload = null
      this.bodyModalIframe = null
    }
  }

  // Create modal in body (like Vue Teleport)
  setupModal() {
    // Create modal HTML and append to body
    const modalHTML = `
      <div class='manage_modal__wrapper hidden' id="file-manager-modal-${this.uploadableIdValue || 'default'}">
        <div class='manage_modal'>
          <header>
            <h1 id="file-manager-modal-title"></h1>
            <div class="button-group">
              <a id="file-manager-modal-download" href='' class='button button--icon button--unfilled'>
                <i class='far fa-download'></i>
              </a>
              <a href='#' id="file-manager-modal-close" class='button button--icon button--unfilled'>
                <i class='far fa-times'></i>
              </a>
            </div>
          </header>
          <main>
            <iframe id="file-manager-modal-iframe" type="application/pdf" width="100%" height="750px"></iframe>
          </main>
        </div>
      </div>
    `
    
    document.body.insertAdjacentHTML('beforeend', modalHTML)
    
    // Store references to the modal elements
    this.modalId = `file-manager-modal-${this.uploadableIdValue || 'default'}`
    this.bodyModalWrapper = document.getElementById(this.modalId)
    this.bodyModalTitle = document.getElementById('file-manager-modal-title')
    this.bodyModalDownload = document.getElementById('file-manager-modal-download')
    this.bodyModalIframe = document.getElementById('file-manager-modal-iframe')
    
    // Add close handler
    document.getElementById('file-manager-modal-close').addEventListener('click', (e) => {
      e.preventDefault()
      this.closeModal()
    })
  }

  // Load files from server
  async loadFiles() {
    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()
      this.uploads = data
      this.updateVisibility()
      this.updateFileCount()
      this.renderFilesList()
    } catch (error) {
      console.error('Error loading files:', error)
      // If we can create files, show the section anyway
      if (this.canCreateValue) {
        this.updateVisibility()
      }
    }
  }

  // Update visibility based on can_create or files existence
  updateVisibility() {
    const shouldShow = this.canCreateValue || this.uploads.length > 0
    
    this.containerTarget.classList.toggle('hidden', !shouldShow)
    
    if (shouldShow) {
      // Show dropzone if can create
      if (this.canCreateValue && this.hasDropzoneContainerTarget) {
        this.dropzoneContainerTarget.classList.remove('hidden')
      }
      
      // Show files list if there are files
      if (this.uploads.length > 0 && this.hasFilesListTarget) {
        this.filesListTarget.classList.remove('hidden')
      }
    }
  }

  // Render files list dynamically
  renderFilesList() {
    if (!this.hasFilesListTarget) return
    
    if (this.uploads.length === 0) {
      this.filesListTarget.innerHTML = ''
      return
    }
    
    const html = this.uploads.map((upload, index) => 
      this.generateFileItemHtml(upload, index)
    ).join('')
    
    this.filesListTarget.innerHTML = html
  }
  
  generateFileItemHtml(upload, index) {
    return `
      <section data-file-manager-target="fileItem" data-index="${index}" data-slug="${upload.slug}">
        ${this.generateEditFormHtml(upload)}
        ${this.generateFileDisplayHtml(upload, index)}
        ${this.generateButtonGroupHtml(upload, index)}
      </section>
    `
  }
  
  generateEditFormHtml(upload) {
    return `
        <form data-file-manager-target="editForm" 
              data-action="submit->file-manager#saveEdit"
              class='asset-sidebar__form hidden'>
          <input type='text' 
                 data-file-manager-target="editInput"
                 placeholder="New name..."
                 value="${upload.name}">
          <div class='button-group'>
            <button type="submit" class='button button--small button--primary'>Save</button>
            <button type="button" 
                    data-action="click->file-manager#cancelEdit"
                    class='button button--small button--secondary'>Cancel</button>
          </div>
        </form>
    `
  }
  
  generateFileDisplayHtml(upload, index) {
    return `
        <div data-file-manager-target="fileDisplay">
          <h3>
            <small class='asset-sidebar__filetype'>${upload.ext || "?"}</small>
            ${upload.ext === "pdf" ? 
              `<a href='#' 
                 data-action="click->file-manager#openPdfModal"
                 data-index="${index}"
                 title='${upload.size} - Uploaded ${upload.created_date}'>
                ${upload.name}
               </a>` :
              `<a href='${upload.url}' 
                 title='${upload.size} - Uploaded ${upload.created_date}'>
                ${upload.name}
               </a>`
            }
          </h3>
        </div>
    `
  }
  
  generateButtonGroupHtml(upload, index) {
    if (!this.canCreateValue || !upload.slug) return ''
    
    return `
        <div class='button-group button-group--condensed'>
          <a href='#' 
             data-action="click->file-manager#startEdit"
             data-index="${index}"
             data-name="${upload.name}"
             class='button button--icon button--unfilled button--icon--small'>
            <i class="fas fa-edit"></i>
          </a>
          <a href='#' 
             data-action="click->file-manager#deleteFile"
             data-slug="${upload.slug}"
             data-index="${index}"
             class='button button--icon button--unfilled button--icon--small button--icon--danger'>
            <i class='fas fa-trash-can'></i>
          </a>
        </div>
    `
  }

  // Ensure Dropzone is initialized for Turbo navigation
  ensureDropzoneInitialized() {
    if (!this.canCreateValue || !this.hasDropzoneTarget) return
    
    // If already initialized, just setup events
    if (this.dropzoneTarget.dropzone) {
      this.setupDropzoneEvents()
      return
    }
    
    // Manually initialize since auto-discovery is disabled
    if (this.dropzoneTarget.classList.contains('dropzone')) {
      new Dropzone(this.dropzoneTarget)
      
      // Setup events after initialization
      setTimeout(() => this.setupDropzoneEvents(), 50)
    }
  }

  // Setup event handlers for auto-discovered Dropzone
  setupDropzoneEvents() {
    if (!this.canCreateValue || !this.hasDropzoneTarget) return
    
    // Find the auto-discovered Dropzone instance
    const dropzoneElement = this.dropzoneTarget
    if (dropzoneElement.dropzone) {
      const dropzone = dropzoneElement.dropzone
      
      // Configure dropzone options (same as vue2-dropzone)
      dropzone.options.timeout = 90000
      dropzone.options.parallelUploads = 10
      dropzone.options.thumbnailWidth = 150
      dropzone.options.maxFilesize = parseInt(this.maxFileSizeValue)
      
      // Add CSRF token and params
      dropzone.options.headers = {
        'X-CSRF-Token': this.csrfToken
      }
      dropzone.options.params = {
        uploadable_id: this.uploadableIdValue,
        uploadable_type: this.uploadableTypeValue
      }
      
      // Setup event handlers (same as vue2-dropzone)
      dropzone.on('success', (file, response) => {
        this.loadFiles()
        flash('File added', 'success')
      })
      
      dropzone.on('error', (file, errorMessage) => {
        flash('Upload failed', 'error')
      })
    }
  }

  // Start editing a file name
  startEdit(event) {
    event.preventDefault()
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    const name = event.currentTarget.dataset.name
    
    // Close any other open edit forms first
    this.clearAllEdits()
    
    if (fileItem) {
      const editForm = fileItem.querySelector('[data-file-manager-target="editForm"]')
      const fileDisplay = fileItem.querySelector('[data-file-manager-target="fileDisplay"]')
      const buttonGroup = fileItem.querySelector('.button-group.button-group--condensed')
      const editInput = fileItem.querySelector('[data-file-manager-target="editInput"]')
      
      fileItem.dataset.editing = 'true'
      fileDisplay.classList.add('hidden')
      editForm.classList.remove('hidden')
      if (buttonGroup) buttonGroup.classList.add('hidden')
      
      if (editInput) {
        editInput.value = name
        editInput.focus()
      }
    }
  }

  // Cancel editing
  cancelEdit(event) {
    event.preventDefault()
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    this.clearEdit(fileItem)
  }

  clearEdit(fileItem) {
    if (!fileItem || fileItem.dataset.editing !== 'true') return
    
    const editForm = fileItem.querySelector('[data-file-manager-target="editForm"]')
    const fileDisplay = fileItem.querySelector('[data-file-manager-target="fileDisplay"]')
    const buttonGroup = fileItem.querySelector('.button-group.button-group--condensed')
    
    editForm.classList.add('hidden')
    fileDisplay.classList.remove('hidden')
    if (buttonGroup) buttonGroup.classList.remove('hidden')
    
    delete fileItem.dataset.editing
  }

  clearAllEdits() {
    this.fileItemTargets
      .filter(item => item.dataset.editing === 'true')
      .forEach(item => this.clearEdit(item))
  }

  // Save edited name
  async saveEdit(event) {
    event.preventDefault()
    
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    if (!fileItem || fileItem.dataset.editing !== 'true') return
    
    const editInput = fileItem.querySelector('[data-file-manager-target="editInput"]')
    const slug = fileItem.dataset.slug
    const newName = editInput.value.trim()
    
    if (!newName) return
    
    try {
      const response = await fetch(`${this.baseUrlValue}/uploads/${slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ name: newName })
      })

      if (response.ok) {
        await this.loadFiles()
        flash('File updated', 'success')
        this.clearEdit(fileItem)
      } else {
        throw new Error('Update failed')
      }
    } catch (error) {
      console.error('Update error:', error)
      flash('Update failed', 'error')
    }
  }

  // Delete a file
  async deleteFile(event) {
    event.preventDefault()
    
    if (!confirm('Are you sure?')) return
    
    const slug = event.currentTarget.dataset.slug
    
    try {
      const response = await fetch(`${this.baseUrlValue}/uploads/${slug}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (response.ok) {
        await this.loadFiles()
      } else {
        throw new Error('Delete failed')
      }
    } catch (error) {
      console.error('Delete error:', error)
      flash('Delete failed', 'error')
    }
  }

  // Open PDF modal
  openPdfModal(event) {
    event.preventDefault()
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    const index = parseInt(fileItem.dataset.index)
    
    if (this.uploads[index]) {
      const upload = this.uploads[index]
      
      if (this.bodyModalTitle) this.bodyModalTitle.textContent = upload.name
      if (this.bodyModalDownload) this.bodyModalDownload.href = upload.url
      if (this.bodyModalIframe) this.bodyModalIframe.src = `${upload.url}?inline=true`
      if (this.bodyModalWrapper) {
        this.bodyModalWrapper.classList.remove('hidden')
        this.bodyModalWrapper.dataset.currentFile = index
      }
    }
  }

  // Close modal
  closeModal(event) {
    if (event) event.preventDefault()
    if (this.bodyModalWrapper) {
      this.bodyModalWrapper.classList.add('hidden')
      delete this.bodyModalWrapper.dataset.currentFile
    }
    if (this.bodyModalIframe) {
      this.bodyModalIframe.src = ''
    }
  }

  // Handle keyboard events
  handleKeydown(event) {
    if (event.key === 'Escape' && this.bodyModalWrapper && this.bodyModalWrapper.dataset.currentFile) {
      this.closeModal()
    }
  }

  // Update file count display  
  updateFileCount() {
    // Find the Files widget toggler controller and update its size
    const allWidgetTogglers = document.querySelectorAll('[data-controller*="widget-toggler"]')
    
    const filesWidgetToggler = Array.from(allWidgetTogglers).find(element => 
      element.dataset.name === 'Files'
    )
    
    if (filesWidgetToggler) {
      filesWidgetToggler.dataset.size = this.uploads.length.toString()
      
      const stimulusController = this.application.getControllerForElementAndIdentifier(filesWidgetToggler, 'widget-toggler')
      
      if (stimulusController) {
        stimulusController.handleSizeUpdate({
          detail: { size: this.uploads.length }
        })
      }
    } else {
      // Fallback: manually update the text
      const textSpan = this.element.querySelector('.asset-sidebar__group--toggle-text')
      if (textSpan) {
        textSpan.textContent = `Files (${this.uploads.length})`
      }
    }
  }
}
